import { Col, Row, Tooltip, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ManufacturingSizeInputField,
  NumberOnlyField,
  SelectField,
} from 'src/components/form';
import {
  FLAT_PIPE_PRODUCT,
  MACHINING_METHOD,
  REGEX,
  ROUND,
  SHAPE,
} from 'src/constants/app';
import useConvertSize from 'src/hooks/useConvertSize';
import useManufacturingSize from 'src/queries/useManufacturingSize';
import useManufacturingSizeDomestic from 'src/queries/useManufacturingSizeDomestic';
import useManufacturingImport from 'src/queries/useManufacturingSizeImport';
import useProductMachining from 'src/queries/useProductMachining';
import {
  checkChangeLanguage,
  convertDimensionsToArray,
  convertShiagsnpoHyojiMojiretsToArray,
  formatManufacturingSizes,
  roundSizeForMachiningMethodFromH2ToH5,
  formatSizeToFiveDecimalsIfMethodIsH2ToH5,
  getSizesWithOriginalValue,
  isDomesticProduct,
  isImportedProduct,
  removeDuplicateObject,
  getUniqueSortedItemsByConvertedValue,
  checkHasDomesticData,
  mapDataOptionsWithFractions,
  getShowProcessingRange,
} from 'src/utils/helper';
import { renderToleranceOptions } from 'src/utils/tolerance';
import { productFormat } from 'src/utils/product';
import {
  getImagePreviewPath,
  getImageIconPreviewPath,
  getProductUnit,
  getSizeByLabelInput,
} from 'src/utils/quotation';
import { FormValue } from '../useForm';
import useSizeInputDisplay from 'src/hooks/useSizeInputDisplay';
import useTolerance from 'src/queries/useTolerance';

type Props = {
  formInfo: UseFormReturn<FormValue, any>;
  product: string;
  machiningMethod: string;
  setLabelError: (e) => void;
  setEstimateErrors: (e) => void;
  setSizes2: (e) => void;
  setSizes3: (e) => void;
  setStockError: (e) => void;
  estimatePriceData?: Response.EstimatePrice['item'];
  dataReEstimate?: Response.EstimatePriceDetail['item'];
  setManufacturingSizes: (e) => void;
  prevLang: string;
  lang: string;
  shape: string;
  dataProductDimension?: Response.ProductDimension['item'];
  isProductFinished: boolean;
  productFinishedInfor: Response.ProductFinishedItem[];
  productWidthData: any;
  productLengthData: any;
  setProductWidthData: (e) => void;
  setProductLengthData: (e) => void;
  setToleranceOptions: (e) => void;
  fractionsData?: Response.Fractions[];
  decimalInputSizeFlags: Payload.DecimalInputSizeFlags | undefined;
  setDecimalInputSizeFlags: (item: Payload.DecimalInputSizeFlags) => void;
  showMachiningTolerance: boolean;
};

const ManuFacturingSize: React.FC<Props> = ({
  formInfo,
  product,
  machiningMethod,
  setLabelError,
  setEstimateErrors,
  setSizes2,
  setSizes3,
  setStockError,
  dataReEstimate,
  estimatePriceData,
  setManufacturingSizes,
  prevLang,
  lang,
  shape,
  dataProductDimension,
  isProductFinished,
  productFinishedInfor,
  productWidthData,
  productLengthData,
  setProductWidthData,
  setProductLengthData,
  setToleranceOptions,
  fractionsData,
  decimalInputSizeFlags,
  setDecimalInputSizeFlags,
  showMachiningTolerance,
}) => {
  const { control, getValues, setValue, watch, clearErrors } = formInfo;
  const { t } = useTranslation();

  const varietyCode = watch('variety');
  const shapeCode = watch('shape');
  const isResetByMachiningMethod = watch('isResetByMachiningMethod');

  const { machiningsData: machinings } = useProductMachining(
    product,
    varietyCode,
    shapeCode,
  );
  const { manufacturingSizesData: manufacturingSizes, isFetching } =
    useManufacturingSize(
      product,
      machiningMethod,
      setEstimateErrors,
      setStockError,
      isProductFinished,
      setDecimalInputSizeFlags,
    );

  const [disableProductWidthInput, setDisableProductWidthInput] =
    useState(false);
  const [disableProductLengthInput, setDisableProductLengthInput] =
    useState(false);
  const [reEstimateWidth, setReEstimateWidth] = useState('');
  const [reEstimateLength, setReEstimateLength] = useState('');
  const [hasReEstimateThickness, setHasReEstimateThickness] = useState(false);
  const [isBothTypes, setIsBothTypes] = useState(false);
  const [isToggleOnBlurSize1, setIsToggleOnBlurSize1] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const [textOriginalOrConvertSize1, setTextOriginalOrConvertSize1] =
    useState<string>('');
  const [textOriginalOrConvertSize2, setTextOriginalOrConvertSize2] =
    useState<string>('');
  const [textOriginalOrConvertSize3, setTextOriginalOrConvertSize3] =
    useState<string>('');

  const productWidthDataRef = useRef(productWidthData);
  const productLengthDataRef = useRef(productLengthData);
  const machiningMethodOfZ0AndH2ToH5 = [
    MACHINING_METHOD.Z0,
    ...MACHINING_METHOD.H2TOH5,
  ];
  const listOptionProductFinshed = useMemo(() => {
    const listOptions = productFinishedInfor.map((item) => ({
      label: item.seihnJoho,
      value: item.seihnJoho,
    }));

    return listOptions;
  }, [productFinishedInfor]);

  const isDomesticProductFlag = isDomesticProduct(product);

  const manufacturingSizeOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo1BunsuHyoji) {
      return manufacturingSizes.sizes;
    }
    return mapDataOptionsWithFractions(
      manufacturingSizes?.sizes,
      fractionsData,
      null,
      null,
      null,
      manufacturingSizes?.snpoBunsuHyojis,
    );
  }, [decimalInputSizeFlags, manufacturingSizes.sizes, fractionsData]);

  const productWidthDataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo2BunsuHyoji) {
      return productWidthData;
    }
    return mapDataOptionsWithFractions(
      productWidthData,
      fractionsData,
      isDomesticProductFlag,
      machiningMethod,
    );
  }, [
    decimalInputSizeFlags,
    productWidthData,
    fractionsData,
    isDomesticProductFlag,
    machiningMethod,
  ]);

  const productLengthDataOptions = useMemo(() => {
    if (!decimalInputSizeFlags?.isSnpo3BunsuHyoji) {
      return productLengthData;
    }
    const isRoundedTo2Digits = true;
    return mapDataOptionsWithFractions(
      productLengthData,
      fractionsData,
      isDomesticProductFlag,
      machiningMethod,
      isRoundedTo2Digits,
    );
  }, [
    decimalInputSizeFlags,
    productLengthData,
    fractionsData,
    isDomesticProductFlag,
    machiningMethod,
  ]);

  const labelHeight = useSizeInputDisplay({
    manufacturingSizes,
    productFinishedInfor,
  });

  const mapToOptions = (arr?: string[]) =>
    arr?.map((item) => ({ originalValue: item, convertedValue: item })) ?? [];

  const handleSuccessDomestic = (
    res: AxiosResponse<Response.ManufacturingSize2AndSize3, any>,
    args: Payload.ManufacturingSizeDomestic,
  ) => {
    if (res?.data?.item) {
      const sizeOptions2 = mapToOptions(res?.data?.item?.sizes2);
      const sizeOptions3 = mapToOptions(res?.data?.item?.sizes3);
      //remove zero and duplicate item in array
      const uniqProductWidthData =
        getUniqueSortedItemsByConvertedValue(sizeOptions2);
      const uniqProductLengthData =
        getUniqueSortedItemsByConvertedValue(sizeOptions3);

      const getSizesWidthData = getSizesWithOriginalValue(
        uniqProductWidthData,
        false,
        ROUND.TO_5_DIGIT,
      );
      const getSizesLengthData = getSizesWithOriginalValue(
        uniqProductLengthData,
        false,
        ROUND.TO_2_DIGIT,
      );
      const hasWidthOptions = !!args?.sizes2;
      const shouldDisplayFraction = hasWidthOptions
        ? decimalInputSizeFlags?.isSnpo3BunsuHyoji
        : decimalInputSizeFlags?.isSnpo2BunsuHyoji;

      //if product width input is empty
      if (!args?.sizes2) {
        if (
          machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) ||
          shouldDisplayFraction
        ) {
          // if isBothTypes === true & productWidthData got from GetManufacturingImportMutation exists
          if (isBothTypes && _.size(productWidthDataRef.current) > 0) {
            const data = getSizesWidthData
              ? removeDuplicateObject([
                  ...productWidthDataRef.current,
                  ...getSizesWidthData,
                ])
              : productWidthDataRef.current;
            if (
              (MACHINING_METHOD.H2TOH5.includes(machiningMethod) &&
                !getSizesWidthData) ||
              _.size(data) > 1
            ) {
              setProductWidthData(data);
              setDisableProductWidthInput(false);
            } else {
              const productWidthValue =
                formatSizeToFiveDecimalsIfMethodIsH2ToH5(
                  productWidthDataRef.current?.[0]?.value,
                  machiningMethod,
                ) || reEstimateWidth;
              const productWidthOptions = [
                {
                  label: productWidthValue,
                  value: productWidthValue,
                  originalValue: productWidthValue,
                },
              ];
              setProductWidthData(productWidthOptions);
              setDisableProductWidthInput(!shouldDisplayFraction);
              if (machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)) {
                setSizes2(
                  productWidthDataRef.current?.[0]?.value || reEstimateWidth,
                );
                !shouldDisplayFraction &&
                  setValue(
                    'product_width',
                    formatSizeToFiveDecimalsIfMethodIsH2ToH5(
                      productWidthDataRef.current?.[0]?.value,
                      machiningMethod,
                    ) || reEstimateWidth,
                  );
              }
            }
          } else {
            //if uniqProductWidthData's size > 1 => show dropdown list
            if (_.size(uniqProductWidthData) > 1) {
              setProductWidthData(getSizesWidthData);
              setDisableProductWidthInput(false);
            }
            //else => autofill
            else {
              if (_.size(uniqProductWidthData) === 0 && isBothTypes) {
                setProductWidthData(undefined);
                setDisableProductWidthInput(false);
                return;
              }
              const productWidthValue =
                formatSizeToFiveDecimalsIfMethodIsH2ToH5(
                  uniqProductWidthData?.[0].convertedValue,
                  machiningMethod,
                ) || reEstimateWidth;
              const productWidthOptions = [
                {
                  label: productWidthValue,
                  value: productWidthValue,
                  originalValue: productWidthValue,
                },
              ];
              setProductWidthData(productWidthOptions);
              setDisableProductWidthInput(!shouldDisplayFraction);
              if (machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)) {
                setSizes2(
                  uniqProductWidthData?.[0].convertedValue || reEstimateWidth,
                );
                !shouldDisplayFraction &&
                  setValue(
                    'product_width',
                    formatSizeToFiveDecimalsIfMethodIsH2ToH5(
                      uniqProductWidthData?.[0].convertedValue,
                      machiningMethod,
                    ) || reEstimateWidth,
                  );
              }
            }
          }
        }
        // if shape === pipe
        else if (shape === SHAPE.PIPE) {
          if (isBothTypes && _.size(productWidthDataRef.current) > 0) {
            setProductWidthData(
              removeDuplicateObject([
                ...productWidthDataRef.current,
                ...getSizesWidthData,
              ]),
            );
          } else {
            setProductWidthData(getSizesWidthData);
          }
          setDisableProductWidthInput(false);
        }
      }
      //if product length label exists
      if (manufacturingSizes?.size2Label && getValues('product_width')) {
        if (
          machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) ||
          shouldDisplayFraction
        ) {
          // if isBothTypes === true & productLengthData got from GetManufacturingImportMutation exists
          if (isBothTypes && _.size(productLengthDataRef.current) > 0) {
            const data = getSizesLengthData
              ? removeDuplicateObject([
                  ...productLengthDataRef.current,
                  ...getSizesLengthData,
                ])
              : productLengthDataRef.current;
            if (
              (MACHINING_METHOD.H2TOH5.includes(machiningMethod) &&
                !getSizesLengthData) ||
              _.size(data) > 1
            ) {
              setProductLengthData(data);
              setDisableProductLengthInput(false);
            } else {
              const productLengthValue =
                roundSizeForMachiningMethodFromH2ToH5(
                  productLengthDataRef.current?.[0]?.value,
                  machiningMethod,
                ) || reEstimateLength;
              const productLengthOptions = [
                {
                  label: productLengthValue,
                  value: productLengthValue,
                  originalValue: productLengthValue,
                },
              ];
              setProductLengthData(productLengthOptions);
              setDisableProductLengthInput(!shouldDisplayFraction);
              if (machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)) {
                setSizes3(
                  productLengthDataRef.current?.[0]?.value || reEstimateLength,
                );
                !shouldDisplayFraction &&
                  setValue(
                    'product_length',
                    roundSizeForMachiningMethodFromH2ToH5(
                      productLengthDataRef.current?.[0]?.value,
                      machiningMethod,
                    ) || reEstimateLength,
                  );
              }
            }
          } else {
            //if uniqProductLengthData's size > 1 => show dropdown list
            if (_.size(uniqProductLengthData) > 1) {
              setProductLengthData(getSizesLengthData);
              setDisableProductLengthInput(false);
            }
            //else => autofill
            else {
              if (_.size(uniqProductLengthData) === 0 && isBothTypes) {
                setProductLengthData(undefined);
                setDisableProductLengthInput(false);
                return;
              }
              const productLengthValue =
                roundSizeForMachiningMethodFromH2ToH5(
                  uniqProductLengthData?.[0].convertedValue,
                  machiningMethod,
                ) || reEstimateLength;
              const productLengthOptions = [
                {
                  label: productLengthValue,
                  value: productLengthValue,
                  originalValue: productLengthValue,
                },
              ];
              setProductLengthData(productLengthOptions);
              setDisableProductLengthInput(!shouldDisplayFraction);
              if (machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)) {
                setSizes3(
                  uniqProductLengthData?.[0].convertedValue || reEstimateLength,
                );
                !shouldDisplayFraction &&
                  setValue(
                    'product_length',
                    roundSizeForMachiningMethodFromH2ToH5(
                      uniqProductLengthData?.[0].convertedValue,
                      machiningMethod,
                    ) || reEstimateLength,
                  );
              }
            }
          }
        }
        // if shape === pipe & isFlatPipeProduct
        else if (shape === SHAPE.PIPE && isFlatPipeProduct()) {
          // if isBothTypes === true & productLengthData got from GetManufacturingImportMutation exists
          if (isBothTypes && _.size(productLengthDataRef.current) > 0) {
            setProductLengthData(
              removeDuplicateObject([
                ...productLengthDataRef.current,
                ...getSizesLengthData,
              ]),
            );
          } else {
            setProductLengthData(getSizesLengthData);
          }
          setDisableProductLengthInput(false);
        }
      }
    }
  };

  const handleSuccessImport = (
    res: AxiosResponse<Response.ManufacturingSize2AndSize3, any>,
    args: Payload.ManufacturingImport,
  ) => {
    if (res?.data?.item) {
      const {
        sizes2,
        sizes3,
        convertAndOriginals = [],
      } = res?.data?.item || {};
      //remove zero and duplicate item in array
      const uniqProductWidthData = getUniqueSortedItemsByConvertedValue(
        sizes2 ? convertAndOriginals : [],
      );
      const uniqProductLengthData = getUniqueSortedItemsByConvertedValue(
        sizes3 ? convertAndOriginals : [],
      );

      // Determine whether to display the fractional size input
      const shouldDisplayFractionSize = args?.sizes2
        ? decimalInputSizeFlags?.isSnpo3BunsuHyoji
        : decimalInputSizeFlags?.isSnpo2BunsuHyoji;

      /**
       * Determine if the product meets the following conditions:
       *   - It is a product of both domestic and imported types (combined).
       *   - It is considered an imported product based on the selected thickness size.
       *   - The size should be displayed in fractional format.
       */
      const isImportedProductWithFractionSize =
        isBothTypes && shouldDisplayFractionSize;
      if (!args?.sizes2) {
        if (
          !(
            !machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) &&
            isBothTypes &&
            shape !== SHAPE.PIPE
          ) ||
          isImportedProductWithFractionSize
        ) {
          setProductWidthData(
            getSizesWithOriginalValue(uniqProductWidthData, false),
          );
          productWidthDataRef.current = getSizesWithOriginalValue(
            uniqProductWidthData,
            false,
          );
        }
      }
      //if product length label exists
      if (manufacturingSizes?.size2Label && getValues('product_width')) {
        if (
          !(
            !machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) &&
            isBothTypes &&
            shape !== SHAPE.PIPE
          ) ||
          isImportedProductWithFractionSize
        ) {
          setProductLengthData(
            getSizesWithOriginalValue(uniqProductLengthData, false),
          );
          productLengthDataRef.current = getSizesWithOriginalValue(
            uniqProductLengthData,
            false,
          );
        }
      }
      //if both types => call api for domestic product
      isBothTypes && handleGetManufacturingSizeDomesticMutation(!!args?.sizes2);
    }
  };

  const handleSuccessTolerance = (
    res: AxiosResponse<Response.Tolerance, any>,
  ) => {
    const data = res?.data?.item;

    const toleranceThicknessOptions = renderToleranceOptions(data?.thickness);
    const toleranceWidthOptions = renderToleranceOptions(data?.width);
    const toleranceLengthOptions = renderToleranceOptions(data?.length);
    const toleranceOutsideDiaOptions = renderToleranceOptions(data?.outsideDia);
    setToleranceOptions({
      thickness: toleranceThicknessOptions,
      width: toleranceWidthOptions,
      length: toleranceLengthOptions,
      outsideDia: toleranceOutsideDiaOptions,
      apiCalled: true,
    });
  };

  const { getManufacturingSizeDomesticMutation } = useManufacturingSizeDomestic(
    {
      handleSuccess: handleSuccessDomestic,
    },
  );
  const { getManufacturingImportMutation } = useManufacturingImport({
    handleSuccess: handleSuccessImport,
  });

  const { getToleranceMutaion } = useTolerance({
    handleSuccess: handleSuccessTolerance,
  });

  useEffect(() => {
    if (product && isDomesticProduct(product) && isImportedProduct(product)) {
      setIsBothTypes(true);
    } else {
      setIsBothTypes(false);
    }
  }, [product]);

  const productSplit = productFormat.splitProductId(product);

  const handleGetToleranceMutaion = () => {
    getToleranceMutaion.mutate({
      shhnCd: productSplit?.shhnCd || undefined,
      hakudoShhnCd: productSplit?.hakudoShhnCd || undefined,
      api1ShhnCd: productSplit?.api1ShhnCd || undefined,
      api2ShhnCd: productSplit?.api2ShhnCd || undefined,
      kakoCd: machiningMethod,
      finishingSize:
        getValues('converted_product_thickness') ||
        getValues('product_thickness'),
    });
  };

  useEffect(() => {
    if (getValues('product_thickness') && !getToleranceMutaion.isLoading) {
      handleGetToleranceMutaion();
    }
  }, [getValues('product_thickness')]);

  useEffect(() => {
    if (
      showMachiningTolerance &&
      (!getValues('machining_thickness') ||
        !getValues('machining_width') ||
        !getValues('machining_length') ||
        !getValues('machining_outside_diameter') ||
        !getValues('machining_inside_diameter'))
    ) {
      handleGetToleranceMutaion();
    }
  }, [
    showMachiningTolerance,
    machiningMethod,
    getValues('machining_thickness'),
    getValues('machining_width'),
    getValues('machining_length'),
    getValues('machining_outside_diameter'),
    getValues('machining_inside_diameter'),
  ]);

  useEffect(() => {
    setLabelError(manufacturingSizes);
    setValue('sizeItem', manufacturingSizes);
    setManufacturingSizes(manufacturingSizes);
    // auto fill value from estimate data from Order history page or product info from Product Detail Page
    if (dataReEstimate && dataReEstimate?.setdnKakoCd && !isFilled) {
      const convertedSizes = convertDimensionsToArray(
        dataReEstimate?.convertedSizes,
      );
      const params = getSizeByLabelInput(
        manufacturingSizes,
        convertedSizes,
        isProductFinished,
      );
      // get params for each dynamic input
      setValue('product_thickness', params.param1);
      handleConvertSize('product_thickness');
      setHasReEstimateThickness(true);
      const isSwappedS2AndS3 = dataReEstimate?.tsuikaJoho?.isSwappedS2AndS3;
      setReEstimateWidth(!isSwappedS2AndS3 ? params.param2 : '');
      setReEstimateLength(!isSwappedS2AndS3 ? params.param3 : '');
      const productWidthOptions = [
        {
          label: params.param2,
          value: params.param2,
          originalValue: params.param2,
        },
      ];
      const productLengthOptions = [
        {
          label: params.param3,
          value: params.param3,
          originalValue: params.param3,
        },
      ];

      !isSwappedS2AndS3 && setProductWidthData(productWidthOptions);
      !isSwappedS2AndS3 && setProductLengthData(productLengthOptions);

      setValue('number_product', dataReEstimate?.inzu?.toString());
      setValue('machining_thickness', dataReEstimate?.optc001 || '');
      setValue('machining_width', dataReEstimate?.optc002 || '');
      setValue('machining_length', dataReEstimate?.optc003 || '');
      dataReEstimate?.optc004 &&
        setValue('machining_outside_diameter', dataReEstimate?.optc004 || '');
      dataReEstimate?.optc005 &&
        setValue('machining_inside_diameter', dataReEstimate?.optc005 || '');
      dataReEstimate?.optc012 &&
        setValue('options_size_unspecified', dataReEstimate?.optc012 || '');
      dataReEstimate?.optSiteiNaiyo012 &&
        setValue(
          'options_size_c',
          (dataReEstimate?.optSiteiNaiyo012 || '').toString(),
        );
      setValue('corner_option_a', (dataReEstimate?.optc013A || '').toString());
      setValue('corner_option_b', (dataReEstimate?.optc013B || '').toString());
      setValue('corner_option_c', (dataReEstimate?.optc013C || '').toString());
      setValue('corner_option_d', (dataReEstimate?.optc013D || '').toString());
      setValue('roll_eyes', dataReEstimate?.optc025 || '');
      setValue('mill_sheets', dataReEstimate?.optc091 || '');
      dataReEstimate?.kyaksChmnNo &&
        setValue('order_number', dataReEstimate?.kyaksChmnNo);
      dataReEstimate?.biko && setValue('order_notes', dataReEstimate?.biko);
      setIsFilled(true);
      return;
    }
    if (dataProductDimension?.snpo1 && !isFilled) {
      setValue('product_thickness', dataProductDimension?.snpo1);
      handleConvertSize('product_thickness');
      setHasReEstimateThickness(true);
      setIsFilled(true);
      return;
    }
  }, [
    manufacturingSizes?.head,
    manufacturingSizes?.size1Label,
    manufacturingSizes?.size2Label,
  ]);

  useEffect(() => {
    // auto fill value from estimate data from Order history page for productFinished
    if (isProductFinished) {
      if (productFinishedInfor?.length > 0 && dataReEstimate && !isFilled) {
        {
          setValue('number_product', dataReEstimate?.inzu?.toString());
          setValue('seihnJoho', dataReEstimate?.seihnJoho);
          dataReEstimate?.kyaksChmnNo &&
            setValue('order_number', dataReEstimate?.kyaksChmnNo);
          dataReEstimate?.biko && setValue('order_notes', dataReEstimate?.biko);
          setIsFilled(true);
          return;
        }
      }
      setValue('number_product', '');
      setValue('seihnJoho', undefined);
      clearErrors('number_product');
      clearErrors('seihnJoho');
    }
  }, [isProductFinished, productFinishedInfor]);

  const handleGetManufacturingSizeDomesticMutation = (hasSize2?: boolean) => {
    getManufacturingSizeDomesticMutation.mutate({
      sizes1: getValues('product_thickness'),
      sizes2:
        hasSize2 && getValues('product_width')
          ? getValues('product_width')
          : undefined,
      sizes3:
        hasSize2 && getValues('product_length')
          ? getValues('product_length')
          : undefined,
      lenghtSize1: manufacturingSizes?.snpoSize || '',
      shhnCd: productSplit?.shhnCd || '',
      kakoCd: machiningMethod,
    });
  };

  const handleGetManufacturingImportMutation = (hasSize2?: boolean) => {
    !hasSize2 && setConversionSize2(undefined);
    setConversionSize3(undefined);

    const valueProductThickness = getValues('product_thickness');
    const matchedProductThickness = manufacturingSizeOptions?.find(
      (item) => item.value === valueProductThickness,
    );

    const valueProductWidth = getValues('product_width');
    const matchedProductWidth = productWidthData?.find(
      (item) => item.value === valueProductWidth,
    );

    getManufacturingImportMutation.mutate({
      api1ShhnCd: productSplit?.api1ShhnCd || undefined,
      api2ShhnCd: productSplit?.api2ShhnCd || undefined,
      hakudoShhnCd: productSplit?.hakudoShhnCd || '',
      sizes1: matchedProductThickness?.originalValue || valueProductThickness,
      sizes2:
        hasSize2 && (matchedProductWidth?.originalValue || valueProductWidth),
      lenghtSize1: manufacturingSizes?.snpoSize || '',
    });
  };

  const {
    handleConvertSize,
    conversionSize1,
    conversionSize2,
    conversionSize3,
    setConversionSize2,
    setConversionSize3,
    conversionSize1MultipleRef,
  } = useConvertSize({
    getValues: getValues,
    manufacturingSizes: manufacturingSizes,
    product: product,
    setValue: setValue,
    estimatePriceData: estimatePriceData,
    handleGetManufacturingImportMutation: handleGetManufacturingImportMutation,
    isFetching: isFetching,
    isFilled: isFilled,
  });

  // Fields for getShowProcessingRange
  const valueProductThickness = watch('product_thickness');
  const valueProductWidth = watch('product_width');
  const valueProductLength = watch('product_length');

  // Safe fallback for manufacturingSizes
  const processingRanges = (manufacturingSizes?.sizes as any) ?? [];

  const showProcessingRange = useMemo(() => {
    const getRawValue = (
      convertedValue,
      fallbackConvertedValue: string,
      options: { originalValue: string; convertedValue: string }[],
    ): string => {
      if (convertedValue) return convertedValue;

      const match = options?.find(
        (item) => item?.convertedValue === fallbackConvertedValue,
      );

      return match?.originalValue ?? fallbackConvertedValue;
    };

    const rawWidth = getRawValue(
      conversionSize2,
      valueProductWidth,
      productWidthData,
    );

    return getShowProcessingRange({
      valueProductThickness,
      valueProductWidth: rawWidth,
      processingRanges,
      machiningMethod,
    });
  }, [
    valueProductThickness,
    valueProductWidth,
    processingRanges,
    machiningMethod,
    conversionSize2,
    productWidthData,
  ]);

  const isFlatPipeProduct = () => {
    return (
      manufacturingSizes?.keijoInfo?.keijoL1Cd ===
        FLAT_PIPE_PRODUCT.KEIJOL1CD &&
      manufacturingSizes?.keijoInfo?.keijoL2Cd ===
        FLAT_PIPE_PRODUCT.KEIJOL2CD &&
      manufacturingSizes?.keijoInfo?.keijoL3Cd === FLAT_PIPE_PRODUCT.KEIJOL3CD
    );
  };

  const handleCallApiSize = (hasSize2?: boolean) => {
    if (isImportedProduct(product)) {
      // if imported product or both types
      handleGetManufacturingImportMutation(hasSize2);
    } else if (isDomesticProduct(product)) {
      let shouldDisplayFraction = hasSize2
        ? decimalInputSizeFlags?.isSnpo3BunsuHyoji
        : decimalInputSizeFlags?.isSnpo2BunsuHyoji;

      // if domestic product
      if (
        machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) ||
        shouldDisplayFraction
      ) {
        handleGetManufacturingSizeDomesticMutation(hasSize2);
      } else {
        if (shape === SHAPE.PIPE) {
          handleGetManufacturingSizeDomesticMutation(hasSize2);
        }
      }
    }
  };

  useEffect(() => {
    if (showProcessingRange?.length > 0) {
      setValue('showProcessingRange', showProcessingRange);
    }
  }, [showProcessingRange]);

  useEffect(() => {
    // clear data when change language
    if (checkChangeLanguage(prevLang, lang)) {
      conversionSize1MultipleRef.current = [];
      setHasReEstimateThickness(false);
      setReEstimateWidth('');
      setReEstimateLength('');
    }
  }, [lang]);

  useEffect(() => {
    // If the processing method changes => automatically fill the value if getValues('product_thickness') exists.
    if (
      isResetByMachiningMethod &&
      getValues('product_thickness') &&
      manufacturingSizes?.snpoSize &&
      !reEstimateWidth &&
      machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)
    ) {
      setValue('product_width', '');
      setValue('product_length', '');
      handleCallApiSize();
    }
  }, [isResetByMachiningMethod, machiningMethod, manufacturingSizes?.snpoSize]);

  useEffect(() => {
    //if reEstimate => auto handleBlur when getValues('product_thickness') exists
    if (
      getValues('product_thickness') &&
      hasReEstimateThickness &&
      !reEstimateWidth &&
      isDomesticProduct(product)
    ) {
      handleCallApiSize(true);
      machiningMethodOfZ0AndH2ToH5.includes(machiningMethod) &&
        setHasReEstimateThickness(false);
    }
  }, [getValues('product_thickness')]);

  useEffect(() => {
    //if reEstimate => set value for product_width and product_length
    if (
      getValues('product_thickness') &&
      !getValues('product_width') &&
      reEstimateWidth
    ) {
      setValue('product_width', reEstimateWidth);
      setReEstimateWidth('');
      handleConvertSize('product_width');
    }
    if (
      getValues('product_width') &&
      !getValues('product_length') &&
      reEstimateLength
    ) {
      setValue('product_length', reEstimateLength);
      setReEstimateLength('');
      handleConvertSize('product_length');
    }
  }, [
    getValues('product_thickness'),
    getValues('product_width'),
    reEstimateWidth,
    reEstimateLength,
  ]);

  //auto call API when sizes2 has only 1 value or have reEstimateWidth
  useEffect(() => {
    if (
      isDomesticProduct(product) &&
      machiningMethodOfZ0AndH2ToH5.includes(machiningMethod)
    ) {
      if (
        (getValues('product_width') || reEstimateWidth) &&
        disableProductWidthInput
      ) {
        handleCallApiSize(true);
      }
    }
  }, [getValues('product_width'), disableProductWidthInput]);

  useEffect(() => {
    //disable if product is domestic and machining method is Z0 or (from H2 to H5 and has domestic data)
    if (
      isDomesticProduct(product) &&
      (machiningMethod === MACHINING_METHOD.Z0 ||
        (MACHINING_METHOD.H2TOH5.includes(machiningMethod) &&
          checkHasDomesticData(manufacturingSizes?.sizes))) &&
      !isFetching
    ) {
      setDisableProductWidthInput(true);
      setDisableProductLengthInput(true);
    } else {
      setDisableProductWidthInput(false);
      setDisableProductLengthInput(false);
    }
  }, [product, machiningMethod, isFetching, isToggleOnBlurSize1]);

  useEffect(() => {
    if (
      estimatePriceData?.hasUpdateFinishingSize &&
      manufacturingSizes?.snpoSize
    ) {
      // example: inputSunpo = '0.0394inch x 0.0492fit x 13.12fit' => inputSunpoArray = ['0.0394','0.0492','13.12']
      const inputSunpoArray = convertDimensionsToArray(
        estimatePriceData?.inputSunpo,
      );
      //example: shiagsnpoHyojiMojirets ='1x15x4000(厚さx辺x長さ)' => shiagsnpoHyojiMojiretsArray = ['1','15','4000']
      const shiagsnpoHyojiMojiretsArray = convertShiagsnpoHyojiMojiretsToArray(
        estimatePriceData?.shiagsnpoHyojiMojirets,
      );
      const dimension = estimatePriceData?.inputSunpo
        ? inputSunpoArray
        : shiagsnpoHyojiMojiretsArray;
      const lenghtSize1 = manufacturingSizes?.snpoSize;
      const productThickness = [...dimension].splice(0, lenghtSize1)?.join('x');
      const productWidth = dimension[lenghtSize1];
      const productLength = dimension[lenghtSize1 + 1];
      setValue('product_thickness', productThickness);
      handleConvertSize('product_thickness');
      setValue('product_width', productWidth);
      handleConvertSize('product_width');
      setValue('product_length', productLength);
      handleConvertSize('product_length');
    }
  }, [estimatePriceData]);

  useEffect(() => {
    renderConversionSize1();
  }, [
    manufacturingSizes,
    conversionSize1,
    conversionSize1MultipleRef?.current,
  ]);

  useEffect(() => {
    renderConversionSizeSingle(conversionSize2, 'product_width');
  }, [manufacturingSizes, conversionSize2]);

  useEffect(() => {
    renderConversionSizeSingle(conversionSize3, 'product_length');
  }, [manufacturingSizes, conversionSize3]);

  useEffect(() => {
    const shouldClear =
      manufacturingSizes?.head && !manufacturingSizes.size2Label;
    if (shouldClear) {
      clearProductLengthValue();
    }
  }, [manufacturingSizes?.head, manufacturingSizes?.size2Label]);

  const clearProductLengthValue = () => {
    setConversionSize3(undefined);
    setProductLengthData(undefined);
    setSizes3('');
    setValue('product_length', '');
    setValue('converted_product_length', '');
    clearErrors('product_length');
  };

  const checkConditionHandleBlurProductThickness = () => {
    // if lenghtSize1 < 6
    if (
      manufacturingSizes?.snpoSize &&
      manufacturingSizes?.snpoSize < 6 &&
      !checkNotGetDataWithImportedProduct()
    ) {
      return true;
    }
    return false;
  };

  const checkConditionHandleBlurProductWidth = () => {
    if (
      // if lenghtSize1 < 5
      manufacturingSizes?.snpoSize &&
      manufacturingSizes?.snpoSize < 5 &&
      !checkNotGetDataWithImportedProduct()
    ) {
      return true;
    }
    return false;
  };

  const checkNotGetDataWithImportedProduct = () => {
    //if isImportedProduct & machining method = KK,KP,FK,FP => not get data from api
    return (
      isImportedProduct(product) &&
      MACHINING_METHOD.EXCLUSION.includes(machiningMethod)
    );
  };

  // Check the label to ensure it contains a fraction
  const isFractionLabel = (label) => {
    if (!label) return false;
    return label.trim().split('/').length > 1;
  };

  const renderConversionSize1 = () => {
    const productThicknessSelected = manufacturingSizeOptions?.find(
      (item) => item?.value === valueProductThickness,
    );

    if (
      decimalInputSizeFlags?.isSnpo1BunsuHyoji &&
      (conversionSize1 || _.size(conversionSize1MultipleRef.current) > 0) &&
      isFractionLabel(productThicknessSelected?.label)
    ) {
      setTextOriginalOrConvertSize1(valueProductThickness);
      return;
    }
    if (_.size(conversionSize1MultipleRef.current) > 0) {
      setTextOriginalOrConvertSize1(
        conversionSize1MultipleRef.current.join('x') +
          manufacturingSizes?.originSize,
      );
      return;
    } else {
      setTextOriginalOrConvertSize1(
        conversionSize1 ? conversionSize1 + manufacturingSizes?.originSize : '',
      );
      return;
    }
  };

  const renderConversionSizeSingle = (conversionSize, inputName) => {
    const productWidthSelected = productWidthDataOptions?.find(
      (item) => item?.value === valueProductWidth,
    );
    const productLengthSelected = productLengthDataOptions?.find(
      (item) => item?.value === valueProductLength,
    );

    if (
      inputName === 'product_width' &&
      decimalInputSizeFlags?.isSnpo2BunsuHyoji &&
      conversionSize &&
      isFractionLabel(productWidthSelected?.label)
    ) {
      setTextOriginalOrConvertSize2(valueProductWidth);
      return;
    }
    if (
      inputName === 'product_length' &&
      decimalInputSizeFlags?.isSnpo3BunsuHyoji &&
      conversionSize &&
      isFractionLabel(productLengthSelected?.label)
    ) {
      setTextOriginalOrConvertSize3(valueProductLength);
      return;
    }

    const valueOriginSize =
      conversionSize && conversionSize + manufacturingSizes?.originSize;
    inputName === 'product_width' &&
      setTextOriginalOrConvertSize2(valueOriginSize);
    inputName === 'product_length' &&
      setTextOriginalOrConvertSize3(valueOriginSize);
    return;
  };

  const handleBlurSizes1 = () => {
    const thickness = getValues('product_thickness');
    if (thickness) {
      const normalized = thickness.replace(/(^|x)\.(\d+)/g, '$10.$2');
      setValue('product_thickness', normalized);
    }

    if (checkConditionHandleBlurProductThickness()) {
      setValue('product_width', '');
      setValue('product_length', '');
      handleCallApiSize();
      setIsToggleOnBlurSize1(!isToggleOnBlurSize1);
    }
    handleConvertSize('product_thickness');
    if (isImportedProduct(product)) {
      getToleranceMutaion.mutate({
        shhnCd: productSplit?.shhnCd || undefined,
        hakudoShhnCd: productSplit?.hakudoShhnCd || undefined,
        api1ShhnCd: productSplit?.api1ShhnCd || undefined,
        api2ShhnCd: productSplit?.api2ShhnCd || undefined,
        kakoCd: machiningMethod,
        finishingSize:
          getValues('converted_product_thickness') ||
          getValues('product_thickness'),
      });
    }
  };

  const handleBlurSizes2 = () => {
    const width = getValues('product_width');
    if (width) {
      const normalized = width.replace(/(^|x)\.(\d+)/g, '$1' + '0.$2');
      setValue('product_width', normalized);
    }
    if (
      checkConditionHandleBlurProductWidth() ||
      decimalInputSizeFlags?.isSnpo3BunsuHyoji
    ) {
      setValue('product_length', '');
      handleCallApiSize(true);
    }
    handleConvertSize('product_width');
  };

  return (
    <>
      <Row className="container_pricing_ManuFacturingSize">
        <Col xs={24} sm={4} md={4} lg={4} xl={4}>
          <Typography.Text className="form_label_required">
            {isProductFinished
              ? t('product_page.product_info_2')
              : t('pricing_page.product_size_field.name')}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={19} md={19} lg={19} xl={19}>
          <Row gutter={[16, 0]}>
            {machinings?.hakudoIllustration && getValues('product') && (
              <Col
                xs={3}
                sm={3}
                md={2}
                lg={3}
                xl={2}
                className="custom_size_img"
              >
                <div
                  className="img_preview_path relative"
                  style={{
                    marginTop: labelHeight,
                  }}
                >
                  <img
                    src={getImageIconPreviewPath(
                      machinings?.hakudoIllustration!,
                    )}
                    style={{
                      top: labelHeight ? `${labelHeight}px` : undefined,
                    }}
                  />
                  <div className="preview">
                    <img
                      src={getImagePreviewPath(machinings?.hakudoIllustration!)}
                    />
                  </div>
                </div>
              </Col>
            )}
            <Col xs={21} sm={21} md={21} lg={21} xl={22}>
              <Row className="input_properties">
                {/* Thickness */}
                {(manufacturingSizes.head || isProductFinished) && (
                  <Col xs={7} md={7} xl={7}>
                    <Row justify={'end'}>
                      <Col xs={24} md={24} xl={24}>
                        {!isProductFinished ? (
                          <ManufacturingSizeInputField<FormValue>
                            useAutoSuggest={
                              !decimalInputSizeFlags?.isSnpo1BunsuHyoji
                            }
                            onBlur={handleBlurSizes1}
                            controller={{
                              control,
                              name: 'product_thickness',
                            }}
                            options={
                              checkNotGetDataWithImportedProduct()
                                ? undefined
                                : manufacturingSizeOptions
                            }
                            label={
                              <Tooltip
                                title={
                                  formatManufacturingSizes(
                                    manufacturingSizes.head,
                                  ) ||
                                  t(
                                    'pricing_page.product_size_field.thickness_field',
                                  )
                                }
                                overlayInnerStyle={{
                                  backgroundColor: '#fff',
                                  color: '#000',
                                }}
                              >
                                <span className="ellipsis-label">
                                  {formatManufacturingSizes(
                                    manufacturingSizes.head,
                                  ) ||
                                    t(
                                      'pricing_page.product_size_field.thickness_field',
                                    )}
                                </span>
                              </Tooltip>
                            }
                            newRegex={REGEX.REGEX_DIMENSIONS_SIZES1}
                            className="size_input custom_size_input"
                            autoCompleteProps={{
                              allowClear: true,
                              maxTagTextLength: 48,
                              filterOption: (inputValue, option) => {
                                if (!getValues('product_thickness'))
                                  return true;
                                const label =
                                  option?.label?.toString().toUpperCase() || '';
                                return label.includes(inputValue.toUpperCase());
                              },
                            }}
                            selectProps={{
                              allowClear: true,
                            }}
                          />
                        ) : (
                          <SelectField<FormValue>
                            controller={{ control, name: 'seihnJoho' }}
                            selectProps={{
                              placeholder: t('pricing_page.type_selection'),
                              allowClear: true,
                              options: listOptionProductFinshed,
                            }}
                            label={t('pricing_page.type_selection')}
                            className="size_input custom_disabled_input custom_size_input"
                          />
                        )}
                      </Col>
                    </Row>
                    <Typography.Text>
                      {textOriginalOrConvertSize1}
                    </Typography.Text>
                    <Typography.Text className="processing-range-text">
                      {showProcessingRange[0]}
                    </Typography.Text>
                  </Col>
                )}

                {/* Width */}
                {manufacturingSizes.size1Label && !isProductFinished && (
                  <>
                    <Col
                      xs={1}
                      sm={1}
                      md={1}
                      lg={1}
                      xl={1}
                      className="after_label"
                    >
                      <Typography.Text className="after_label_text">
                        {t('pricing_page.product_size_field.thickness_after')}
                      </Typography.Text>
                    </Col>
                    <Col xs={7} sm={7} md={7} lg={7} xl={7}>
                      <ManufacturingSizeInputField<FormValue>
                        useAutoSuggest={
                          !decimalInputSizeFlags?.isSnpo2BunsuHyoji
                        }
                        onBlur={handleBlurSizes2}
                        controller={{ control, name: 'product_width' }}
                        options={productWidthDataOptions}
                        label={
                          <Tooltip
                            title={
                              formatManufacturingSizes(
                                manufacturingSizes.size1Label,
                              ) ||
                              t('pricing_page.product_size_field.width_field')
                            }
                            overlayInnerStyle={{
                              backgroundColor: '#fff',
                              color: '#000',
                            }}
                          >
                            <span className="ellipsis-label">
                              {formatManufacturingSizes(
                                manufacturingSizes.size1Label,
                              ) ||
                                t(
                                  'pricing_page.product_size_field.width_field',
                                )}
                            </span>
                          </Tooltip>
                        }
                        newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                        className="size_input custom_disabled_input custom_size_input"
                        autoCompleteProps={{
                          allowClear: true,
                          maxTagTextLength: 10,
                          showSearch: true,
                          filterOption: false,
                          showArrow: false,
                          disabled: disableProductWidthInput,
                          dropdownClassName:
                            machiningMethod === MACHINING_METHOD.KP &&
                            'hiddenAutoCompleteOptions',
                        }}
                        selectProps={{
                          allowClear: true,
                        }}
                      />
                      <Typography.Text>
                        {textOriginalOrConvertSize2}
                      </Typography.Text>
                      <Typography.Text className="processing-range-text">
                        {showProcessingRange[1]}
                      </Typography.Text>
                    </Col>
                  </>
                )}

                {/* Length */}
                {manufacturingSizes.size2Label && !isProductFinished && (
                  <>
                    <Col
                      xs={1}
                      sm={1}
                      md={1}
                      lg={1}
                      xl={1}
                      className="after_label"
                    >
                      <Typography.Text className="after_label_text">
                        {t('pricing_page.product_size_field.width_after')}
                      </Typography.Text>
                    </Col>
                    <Col xs={7} sm={7} md={7} lg={7} xl={7}>
                      <ManufacturingSizeInputField<FormValue>
                        useAutoSuggest={
                          !decimalInputSizeFlags?.isSnpo3BunsuHyoji
                        }
                        onBlur={() => {
                          handleConvertSize('product_length');
                        }}
                        controller={{ control, name: 'product_length' }}
                        options={productLengthDataOptions}
                        label={
                          <Tooltip
                            title={
                              formatManufacturingSizes(
                                manufacturingSizes.size2Label,
                              ) ||
                              t('pricing_page.product_size_field.length_field')
                            }
                            overlayInnerStyle={{
                              backgroundColor: '#fff',
                              color: '#000',
                            }}
                          >
                            <span className="ellipsis-label">
                              {formatManufacturingSizes(
                                manufacturingSizes.size2Label,
                              ) ||
                                t(
                                  'pricing_page.product_size_field.length_field',
                                )}
                            </span>
                          </Tooltip>
                        }
                        newRegex={REGEX.REGEX_DIMENSIONS_SIZES2_SIZES3}
                        className="size_input custom_disabled_input custom_size_input"
                        autoCompleteProps={{
                          allowClear: true,
                          maxTagTextLength: 10,
                          showSearch: true,
                          filterOption: false,
                          disabled: disableProductLengthInput,
                          dropdownClassName:
                            machiningMethod === MACHINING_METHOD.KP &&
                            'hiddenAutoCompleteOptions',
                        }}
                        selectProps={{
                          allowClear: true,
                        }}
                      />

                      <Typography.Text>
                        {textOriginalOrConvertSize3}
                      </Typography.Text>
                      <Typography.Text className="processing-range-text">
                        {showProcessingRange[2]}
                      </Typography.Text>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Quantity */}
      {(manufacturingSizes.head || isProductFinished) && (
        <Row className="quantity_row" align="middle">
          <Col xs={24} sm={4} md={4} xl={4}>
            <Typography.Text className="form_label_required">
              {t('pricing_page.product_size_field.number_field')}
            </Typography.Text>
          </Col>
          <Col xs={24} sm={10} md={10} lg={10} xl={10}>
            <NumberOnlyField<FormValue>
              controller={{ control, name: 'number_product' }}
              inputProps={{
                placeholder: t('pricing_page.product_size_field.number_field'),
                maxLength: 9,
                suffix: isProductFinished
                  ? t('pricing_page.sheet')
                  : getProductUnit(getValues('shape')),
              }}
              isOnlyInteger={true}
            />
            {/* <Row align="middle" gutter={[8, 0]}>
              <Col xs={20} sm={16} md={10} lg={8} xl={6}>
               
              </Col> */}
            {/* <Col xs={4} sm={4} md={3} lg={2} xl={2} className="after_label">
                <Typography.Text className="after_label_text">
                  {isProductFinished
                    ? t('pricing_page.sheet')
                    : getProductUnit(getValues('shape'))}
                </Typography.Text>
              </Col> */}
            {/* </Row> */}
          </Col>
        </Row>
      )}
    </>
  );
};

export default ManuFacturingSize;
